import React, { useEffect } from 'react';
import { Title2 } from '../../components/typography/Typography';
import 'twin.macro';
import { useQuery } from 'hooks/sympl-query';
import { User } from 'types/userTypes';
import useCareerPageContext from 'hooks/context/career-page-context';
import { GET_VERSION_HISTORY } from 'graphql/careers/queries';
import tw from 'twin.macro';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import ToolTip from 'components/tooltip/ToolTip';
import { useMutation } from 'hooks/sympl-mutation';
import { RESTORE_VERSION_HISTORY } from 'graphql/careers/mutations';
import Button from 'components/button/Button';

interface VersionHistoryItem {
  id: number;
  isCurrent: boolean;
  timestamp: string;
  user: User;
}

const VersionHistory: React.FC<{
  pId?: string;
  refetchCareerPage: () => void;
  onRestore: () => void;
}> = ({ pId, refetchCareerPage, onRestore }) => {
  const {
    careerPageConfig,
    versionHistoryModalOpen,
    storesItself,
    waiting,
    restoreVersion,
  } = useCareerPageContext();
  /** The version whose restore is being asked about, and what went wrong. */
  const [asking, setAsking] = React.useState<number | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pageId, _] = React.useState<string>(pId!);
  const [versionHistory, setVersionHistory] = React.useState<
    VersionHistoryItem[]
  >([]);

  const { loading: historyIsLoading, refetch } = useQuery<
    {
      versionHistory: {
        history: VersionHistoryItem[];
      };
    },
    { pageId: string }
  >(GET_VERSION_HISTORY, {
    skip: !careerPageConfig || !versionHistoryModalOpen || !pageId,
    variables: { pageId: `${pageId}` },
    onCompleted: (data) => setVersionHistory(data.versionHistory.history),
  });

  useEffect(() => {
    refetch();
  }, []);

  const [restoreVersionHistory] = useMutation<
    { createVersionHistory: { history: VersionHistoryItem[] } },
    { pageId: number; input: {}; historyId?: number }
  >(RESTORE_VERSION_HISTORY);

  /*
   * Auto-save (scene 23, D37): the earlier version is put on the page as an
   * ordinary change. No "are you sure?": Undo brings the page back. Only
   * when changes are already waiting on an Active page it asks once, in the
   * description's words.
   */
  const waitingCount = waiting?.status === 'ACTIVE' ? waiting?.count ?? 0 : 0;
  const putBack = (id: number) => {
    setProblem(null);
    restoreVersion(id).then(
      () => {
        setAsking(null);
        onRestore();
      },
      () => setProblem('Could not read that version. Try again.')
    );
  };

  const restoreHistoryPoint = (id: number) => {
    if (storesItself) {
      if (waitingCount > 0) setAsking(id);
      else putBack(id);
      return;
    }
    restoreVersionHistory({
      variables: {
        pageId: +pageId,
        input: {},
        historyId: id,
      },
    }).then(() => {
      onRestore();
      refetchCareerPage();
    });
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    const today = new Date();
    const isToday =
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear();

    return isToday
      ? `Today, ${d.getHours()}:${d.getMinutes()}`
      : `${d.toLocaleString('default', {
          month: 'short',
        })} ${d.getDate()}, ${d.getFullYear()} - ${d.getHours()}:${String(
          d.getMinutes()
        ).padStart(2, '0')}`;
  };

  if (historyIsLoading)
    return (
      <div tw="h-full flex flex-col gap-8">
        <Title2 mt={1}>Version History</Title2>
        <div tw="w-full text-center">
          <p>Loading...</p>
        </div>
      </div>
    );

  return (
    <div tw="h-full flex flex-col gap-8">
      <Title2 mt={1}>Version History</Title2>

      {asking !== null && (
        <div
          tw="flex flex-col gap-3 p-3 rounded-md border-2 border-gray-200"
          data-testid="careers-restore-question"
        >
          <p tw="text-sm text-gray-700">
            {`This replaces ${waitingCount} ${
              waitingCount === 1 ? 'change that is' : 'changes that are'
            } not yet public.`}
          </p>
          <div tw="flex gap-3 justify-end">
            <Button
              variant="secondary"
              testId="careers-restore-cancel"
              onClick={() => setAsking(null)}
            >
              Cancel
            </Button>
            <Button
              testId="careers-restore-confirm"
              onClick={() => putBack(asking)}
            >
              Restore
            </Button>
          </div>
        </div>
      )}
      {problem && (
        <p tw="text-sm text-red-600" data-testid="careers-restore-problem">
          {problem}
        </p>
      )}

      {!versionHistory.length ? (
        <div tw="w-full text-center">
          <p>There are no previous versions yet.</p>
        </div>
      ) : (
        <div tw="flex flex-col-reverse gap-4 w-full">
          {versionHistory.map((item) => (
            <div
              key={item.id}
              className="group"
              tw="flex items-center justify-between py-2 px-3 rounded-md border-2"
              data-testid="careers-version"
              css={[
                item.isCurrent
                  ? tw`bg-indigo-50 border-indigo-200 hover:(bg-indigo-100 border-indigo-300)`
                  : tw`bg-gray-100 border-gray-200 hover:(bg-gray-200 border-gray-300)`,
              ]}
            >
              <div tw="flex gap-4 items-center">
                {/* Avatar */}
                {/* TODO: Change to user avatar when added - 23/10/2024 */}
                <div tw="w-9 h-9 rounded-full bg-white grid place-items-center text-base">
                  {item.user.firstname.charAt(0)}
                  {item.user.lastname.charAt(0)}
                </div>

                {/* Text */}
                <div>
                  <p>{formatDate(item.timestamp)}</p>
                  <p tw="text-sm">{`${item.user.firstname} ${item.user.lastname}`}</p>
                </div>
              </div>

              {/* actions (set this verison back) */}
              {!item.isCurrent && (
                <ToolTip text="Restore this version" placement="bottom" arrow>
                  <ClockCounterClockwise
                    tw="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    weight="regular"
                    size={20}
                    data-testid="careers-restore-version"
                    onClick={() => restoreHistoryPoint(item.id)}
                  />
                </ToolTip>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
